import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaTimes, FaSave, FaPlus, FaTrash, FaWrench, FaSearch, FaFileImport } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  Template,
  SchemaField,
  createTemplate,
  updateTemplate,
  getTemplate,
  loadSchema,
  TemplateCreatePayload,
  TemplateUpdatePayload,
} from '../services/TemplatesService';
import { TemplateBindingsPanel } from './TemplateBindingsPanel';
import { MINIJOB_SCHEMA_V2 } from '../utils/schemaTemplates';

interface TemplateEditorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: number | null;
  onSaved: () => void;
}

type TabType = 'meta' | 'content' | 'schema' | 'blocks' | 'bindings';

export function TemplateEditorDrawer({
  isOpen,
  onClose,
  templateId,
  onSaved,
}: TemplateEditorDrawerProps) {
  const [activeTab, setActiveTab] = useState<TabType>('meta');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [templateKey, setTemplateKey] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const [active, setActive] = useState(true);
  const [containsCompensation, setContainsCompensation] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [requiredFields, setRequiredFields] = useState<SchemaField[]>([]);
  const [optionalFields, setOptionalFields] = useState<SchemaField[]>([]);
  const [blocksSchema, setBlocksSchema] = useState<any[]>([]);
  const [schemaFamily, setSchemaFamily] = useState<string | undefined>();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const htmlTextareaRef = useRef<HTMLTextAreaElement>(null);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e0e3ef',
    borderRadius: 6,
    fontSize: 14,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 500,
    color: '#666',
  };

  useEffect(() => {
    if (isOpen && templateId) {
      loadTemplate();
    } else if (isOpen && !templateId) {
      resetForm();
    }
  }, [isOpen, templateId]);

  const loadTemplate = async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const data = await getTemplate(templateId);
      setTemplateKey(data.template_key);
      setName(data.name);
      setLanguage(data.language);
      setActive(data.active);
      setContainsCompensation(data.contains_compensation);
      setHtmlContent(data.html_content || '');
      setRequiredFields(data.schema?.required_fields || []);
      setOptionalFields(data.schema?.optional_fields || []);
      setBlocksSchema(Array.isArray(data.blocks_schema) ? data.blocks_schema : []);
      setSchemaFamily(data.schema_family);
    } catch (err: any) {
      console.error('Failed to load template:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(`Failed to load template${traceId ? ` (Trace: ${traceId})` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTemplateKey('');
    setName('');
    setLanguage('de');
    setActive(true);
    setContainsCompensation(false);
    setHtmlContent('');
    setRequiredFields([]);
    setOptionalFields([]);
    setSchemaFamily(undefined);
    setValidationErrors({});
    setActiveTab('meta');
    setSelectedKey(null);
  };

  const extractVariablesFromHTML = (html: string): string[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(html)) !== null) {
      const fullMatch = match[1].trim();
      const key = fullMatch.split('|')[0].trim();
      if (!matches.includes(key)) {
        matches.push(key);
      }
    }
    return matches;
  };

  const allSchemaFields = useMemo(() => {
    const reqFields = Array.isArray(requiredFields) ? requiredFields : [];
    const optFields = Array.isArray(optionalFields) ? optionalFields : [];
    return [...reqFields, ...optFields];
  }, [requiredFields, optionalFields]);

  const editableInWizardKeys = useMemo(() => {
    return allSchemaFields
      .filter((f) => f.editable_in === 'wizard')
      .map((f) => f.key);
  }, [allSchemaFields]);

  const variablesInHTML = useMemo(() => {
    return extractVariablesFromHTML(htmlContent);
  }, [htmlContent]);

  const scrollToVariable = (key: string) => {
    if (!htmlTextareaRef.current) return;
    const textarea = htmlTextareaRef.current;
    const searchPattern = `{{${key}}}`;
    const index = textarea.value.indexOf(searchPattern);
    if (index !== -1) {
      textarea.focus();
      textarea.setSelectionRange(index, index + searchPattern.length);
      textarea.scrollTop = Math.max(0, (index / textarea.value.length) * textarea.scrollHeight - 100);
      setSelectedKey(key);
    }
  };

  const validateSchema = (): boolean => {
    const errors: Record<string, string> = {};
    const reqFields = Array.isArray(requiredFields) ? requiredFields : [];
    const optFields = Array.isArray(optionalFields) ? optionalFields : [];
    const allFields = [...reqFields, ...optFields];
    const keys = allFields.map((f) => f.key);
    const variablesInHTML = extractVariablesFromHTML(htmlContent);

    allFields.forEach((field, idx) => {
      if (!field.key) {
        errors[`${field.key || idx}_key`] = 'Key is required';
      }
      if (!field.label) {
        errors[`${field.key || idx}_label`] = 'Label is required';
      }
      if (keys.filter((k) => k === field.key).length > 1) {
        errors[`${field.key}_duplicate`] = `Key "${field.key}" is duplicated`;
      }
      if (!['string', 'number', 'decimal', 'date', 'select', 'text'].includes(field.type)) {
        errors[`${field.key}_type`] = 'Invalid type';
      }
      if (field.type === 'select' && (!field.options || field.options.length === 0)) {
        errors[`${field.key}_options`] = 'Select type requires options';
      }
      if (field.editable_in === 'wizard' && !variablesInHTML.includes(field.key)) {
        errors[`${field.key}_missing_html`] = `Field "${field.key}" is editable in wizard but missing in HTML`;
      }
    });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      console.error('Schema validation errors:', errors);
    }
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!templateId && !templateKey.trim()) {
      toast.error('Template Key is required');
      setActiveTab('meta');
      return;
    }
    if (!templateId) {
      const slugRegex = /^[a-zA-Z0-9_-]+$/;
      if (!slugRegex.test(templateKey.trim())) {
        toast.error('Template Key must only contain letters, numbers, underscores (_) and hyphens (-). No spaces or special characters allowed.');
        setActiveTab('meta');
        return;
      }
    }
    if (!name.trim()) {
      toast.error('Template Name is required');
      setActiveTab('meta');
      return;
    }
    if (!validateSchema()) {
      toast.error('Please fix validation errors');
      setActiveTab('schema');
      return;
    }

    setSaving(true);
    try {
      const schema = {
        required_fields: requiredFields,
        optional_fields: optionalFields,
      };

      if (templateId) {
        const payload: TemplateUpdatePayload = {
          name,
          language,
          active,
          contains_compensation: containsCompensation,
          html_content: htmlContent,
          schema,
        };
        await updateTemplate(templateId, payload);
        toast.success('Template updated successfully');
      } else {
        const payload: TemplateCreatePayload = {
          template_key: templateKey,
          name,
          language,
          active,
          contains_compensation: containsCompensation,
          html_content: htmlContent,
          schema,
        };
        await createTemplate(payload);
        toast.success('Template created successfully');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('Failed to save template:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      const status = err?.response?.status;
      const errorData = err?.response?.data;

      if (status === 422) {

        let errorMessage = errorData?.detail || errorData?.message || 'Validation failed';

        // Zeige spezifische Feld-Fehler an
        if (errorData?.details) {
          const fieldErrors = Object.entries(errorData.details)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('\n');
          errorMessage = `Validation errors:\n${fieldErrors}`;
        }

        toast.error(
          <div>
            <div>{errorMessage}</div>
            {traceId && <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Trace ID: {traceId}</div>}
          </div>
        );
      } else {
        toast.error(
          <div>
            <div>Failed to save template</div>
            {traceId && <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Trace ID: {traceId}</div>}
          </div>
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const addField = (isRequired: boolean) => {
    const newField: SchemaField = {
      key: '',
      label: '',
      type: 'string',
      default_value: undefined,
      options: undefined,
      help_text: '',
      profile_map: undefined,
      editable_in: 'wizard',
      step_hint: undefined,
    };
    if (isRequired) {
      const current = Array.isArray(requiredFields) ? requiredFields : [];
      setRequiredFields([...current, newField]);
    } else {
      const current = Array.isArray(optionalFields) ? optionalFields : [];
      setOptionalFields([...current, newField]);
    }
  };

  const updateField = (
    isRequired: boolean,
    index: number,
    updates: Partial<SchemaField>
  ) => {
    const current = isRequired
      ? (Array.isArray(requiredFields) ? requiredFields : [])
      : (Array.isArray(optionalFields) ? optionalFields : []);
    const fields = [...current];
    fields[index] = { ...fields[index], ...updates };
    if (isRequired) {
      setRequiredFields(fields);
    } else {
      setOptionalFields(fields);
    }
  };

  const removeField = (isRequired: boolean, index: number) => {
    if (isRequired) {
      const current = Array.isArray(requiredFields) ? requiredFields : [];
      setRequiredFields(current.filter((_, i) => i !== index));
    } else {
      const current = Array.isArray(optionalFields) ? optionalFields : [];
      setOptionalFields(current.filter((_, i) => i !== index));
    }
  };

  const loadMinijobSchema = async () => {
    if (!templateId) {
      toast.error('Kein Template geladen');
      return;
    }

    if (requiredFields.length > 0 || optionalFields.length > 0) {
      const confirmed = window.confirm(
        'Loading Minijob schema will replace all existing schema fields. Continue?'
      );
      if (!confirmed) return;
    }

    try {
      const response = await loadSchema(templateId, { schema_family: 'MINIJOB_BASE_V2' });
      setRequiredFields(response.required_fields);
      setOptionalFields(response.optional_fields);
      const totalFields = response.required_fields.length + response.optional_fields.length;
      toast.success(
        <div>
          <div>Minijob Schema V2 geladen ({totalFields} Felder: {response.required_fields.length} required, {response.optional_fields.length} optional)!</div>
          <div style={{ fontSize: '0.85em', marginTop: '4px' }}>
            ✅ Vergiss nicht: Meta-Tab → "Contains Compensation Data 🔒" aktivieren
          </div>
        </div>,
        { autoClose: 5000 }
      );
    } catch (err: any) {
      console.error('Failed to load schema:', err);
      const errorMsg = err?.response?.data?.message || err?.response?.data?.detail || 'Schema konnte nicht geladen werden';
      const traceId = err?.response?.headers?.['x-trace-id'];

      // 400 = Family mismatch
      if (err?.response?.status === 400) {
        toast.error(
          <div>
            <div><strong>Schema Family Mismatch!</strong></div>
            <div style={{ fontSize: '0.9em', marginTop: '4px' }}>{errorMsg}</div>
            {traceId && <div style={{ fontSize: '0.8em', marginTop: '4px' }}>Trace: {traceId}</div>}
          </div>,
          { autoClose: 8000 }
        );
      } else {
        toast.error(`Fehler: ${errorMsg}${traceId ? ` (Trace: ${traceId})` : ''}`);
      }
    }
  };

  const loadCondSchema = async () => {
    if (!templateId) {
      toast.error('Kein Template geladen');
      return;
    }

    if (requiredFields.length > 0 || optionalFields.length > 0) {
      const confirmed = window.confirm(
        'Loading COND schema will replace all existing schema fields. Continue?'
      );
      if (!confirmed) return;
    }

    try {
      const response = await loadSchema(templateId, { schema_family: 'MINIJOB_COND_V1' });
      setRequiredFields(response.required_fields);
      setOptionalFields(response.optional_fields);
      const totalFields = response.required_fields.length + response.optional_fields.length;
      toast.success(
        <div>
          <div>Minijob COND Schema V1 geladen ({totalFields} Felder: {response.required_fields.length} required, {response.optional_fields.length} optional)!</div>
          <div style={{ fontSize: '0.85em', marginTop: '4px' }}>
            ✅ Inkl. § 1 Befristung (3) + § 7 Adresse (4) | Basis 33 + 7 neue = 39 | Meta-Tab → "Contains Compensation Data 🔒"
          </div>
        </div>,
        { autoClose: 6000 }
      );
    } catch (err: any) {
      console.error('Failed to load COND schema:', err);
      const errorMsg = err?.response?.data?.message || err?.response?.data?.detail || 'Schema konnte nicht geladen werden';
      const traceId = err?.response?.headers?.['x-trace-id'];

      // 400 = Family mismatch
      if (err?.response?.status === 400) {
        toast.error(
          <div>
            <div><strong>Schema Family Mismatch!</strong></div>
            <div style={{ fontSize: '0.9em', marginTop: '4px' }}>{errorMsg}</div>
            {traceId && <div style={{ fontSize: '0.8em', marginTop: '4px' }}>Trace: {traceId}</div>}
          </div>,
          { autoClose: 8000 }
        );
      } else {
        toast.error(`Fehler: ${errorMsg}${traceId ? ` (Trace: ${traceId})` : ''}`);
      }
    }
  };

  const renderFieldEditor = (field: SchemaField, isRequired: boolean, index: number) => {
    const fieldErrors = Object.keys(validationErrors).filter((key) =>
      key.startsWith(field.key || index.toString())
    );

    return (
      <div
        key={index}
        style={{
          padding: 16,
          border: fieldErrors.length > 0 ? '1px solid #F44336' : '1px solid #e0e3ef',
          borderRadius: 6,
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Key *</label>
            <input
              type="text"
              value={field.key}
              onChange={(e) => updateField(isRequired, index, { key: e.target.value })}
              style={inputStyle}
              placeholder="field_key"
            />
          </div>
          <div>
            <label style={labelStyle}>Label *</label>
            <input
              type="text"
              value={field.label}
              onChange={(e) => updateField(isRequired, index, { label: e.target.value })}
              style={inputStyle}
              placeholder="Field Label"
            />
          </div>
          <div>
            <label style={labelStyle}>Type *</label>
            <select
              value={field.type}
              onChange={(e) =>
                updateField(isRequired, index, {
                  type: e.target.value as SchemaField['type'],
                })
              }
              style={inputStyle}
            >
              <option value="string">String</option>
              <option value="number">Number</option>
              <option value="decimal">Decimal</option>
              <option value="date">Date</option>
              <option value="select">Select</option>
              <option value="text">Text (multi-line)</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>
              Editable In {field.type === 'text' && '(Text blocks)'}
            </label>
            <select
              value={field.editable_in || 'wizard'}
              onChange={(e) =>
                updateField(isRequired, index, {
                  editable_in: e.target.value as 'wizard' | 'template',
                })
              }
              style={inputStyle}
            >
              <option value="wizard">Wizard 🔧</option>
              <option value="template">Template Only</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Profile Map</label>
            <input
              type="text"
              value={field.profile_map ?? ''}
              onChange={(e) => updateField(isRequired, index, { profile_map: e.target.value || undefined })}
              style={inputStyle}
              placeholder="employee.field_name"
            />
          </div>
          <div>
            <label style={labelStyle}>Step Hint</label>
            <select
              value={field.step_hint || ''}
              onChange={(e) =>
                updateField(isRequired, index, {
                  step_hint: e.target.value ? (e.target.value as any) : undefined,
                })
              }
              style={inputStyle}
            >
              <option value="">None</option>
              <option value="basic_info">Basic Info</option>
              <option value="compensation">Compensation</option>
              <option value="clauses">Clauses</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Default Value</label>
            {field.type === 'text' ? (
              <textarea
                value={field.default_value ?? ''}
                onChange={(e) => updateField(isRequired, index, { default_value: e.target.value })}
                rows={3}
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
                placeholder="Default text..."
              />
            ) : (
              <input
                type="text"
                value={field.default_value ?? ''}
                onChange={(e) => updateField(isRequired, index, { default_value: e.target.value })}
                style={inputStyle}
                placeholder="Default value..."
              />
            )}
          </div>
          <div>
            <label style={labelStyle}>Help Text</label>
            <input
              type="text"
              value={field.help_text ?? ''}
              onChange={(e) => updateField(isRequired, index, { help_text: e.target.value })}
              style={inputStyle}
              placeholder="Help text..."
            />
          </div>
        </div>

        {field.type === 'select' && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Options (JSON)</label>
            <textarea
              value={JSON.stringify(field.options || [], null, 2)}
              onChange={(e) => {
                try {
                  const options = JSON.parse(e.target.value);
                  updateField(isRequired, index, { options });
                } catch {
                  // Invalid JSON, ignore
                }
              }}
              style={{ ...inputStyle, minHeight: 80, fontFamily: 'monospace' }}
              placeholder='[{"value": "opt1", "label": "Option 1"}]'
            />
          </div>
        )}

        {fieldErrors.length > 0 && (
          <div style={{ marginBottom: 8, color: '#F44336', fontSize: 12 }}>
            {fieldErrors.map((key) => (
              <div key={key}>{validationErrors[key]}</div>
            ))}
          </div>
        )}

        <button
          onClick={() => removeField(isRequired, index)}
          style={{
            padding: '6px 12px',
            background: '#ffebee',
            border: '1px solid #d32f2f',
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 600,
            color: '#d32f2f',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <FaTrash size={10} />
          Remove
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '700px',
        backgroundColor: 'white',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease',
      }}
    >
      <div
        style={{
          padding: 24,
          borderBottom: '1px solid #e0e3ef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0f2b5a' }}>
          {templateId ? 'Edit Template' : 'Create Template'}
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 8,
            color: '#666',
          }}
        >
          <FaTimes size={18} />
        </button>
      </div>

      <div style={{ padding: '16px 24px 0 24px' }}>
        <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
          {(['meta', 'content', 'schema', 'blocks', 'bindings'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px 4px',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: activeTab === tab ? 600 : 500,
                color: activeTab === tab ? '#021a4a' : '#666',
                borderBottom: activeTab === tab ? '3px solid #021a4a' : '3px solid transparent',
                transition: 'all 0.2s ease',
              }}
            >
              {tab === 'meta' ? 'Meta' :
               tab === 'content' ? 'Content' :
               tab === 'schema' ? 'Schema' :
               tab === 'blocks' ? 'Blocks' :
               'Bindings'}
            </button>
          ))}
        </div>
        <div style={{ height: 1, backgroundColor: '#e0e3ef' }} />
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
            Loading template...
          </div>
        ) : (
          <>
            {activeTab === 'meta' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ padding: 16, background: '#e8f5e9', borderRadius: 6, border: '1px solid #4caf50' }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#2e7d32', fontWeight: 600, marginBottom: 8 }}>
                    💡 Empfohlene Einstellungen für Minijob-Verträge:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#388e3c' }}>
                    <li>Template Key: <code>CONTRACT_SERVICE_MINIJOB_DE_V2</code></li>
                    <li>Name: "Arbeitsvertrag – Service (Minijob, unbefristet)"</li>
                    <li>Language: German (de)</li>
                    <li>Active: ✅ (aktiviert)</li>
                    <li>Contains Compensation Data 🔒: ✅ (aktiviert, da hourly_rate enthalten)</li>
                  </ul>
                </div>

                {!templateId && (
                  <div>
                    <label style={labelStyle}>Template Key *</label>
                    <input
                      type="text"
                      value={templateKey}
                      onChange={(e) => setTemplateKey(e.target.value)}
                      style={inputStyle}
                      placeholder="CONTRACT_SERVICE_MINIJOB_DE_V2"
                      required
                    />
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      Unique identifier for this template (e.g., CONTRACT_SERVICE_MINIJOB_DE_V2).
                      Only letters, numbers, underscores (_) and hyphens (-) allowed. No spaces.
                    </div>
                  </div>
                )}

                <div>
                  <label style={labelStyle}>Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={inputStyle}
                    placeholder="Arbeitsvertrag – Service (Minijob, unbefristet)"
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle}>Language *</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as 'de' | 'en')}
                    style={inputStyle}
                  >
                    <option value="de">German</option>
                    <option value="en">English</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 14, fontWeight: 500 }}>Active</span>
                  </label>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={containsCompensation}
                      onChange={(e) => setContainsCompensation(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 14, fontWeight: 500 }}>Contains Compensation Data 🔒</span>
                  </label>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4, marginLeft: 24 }}>
                    Requires PIN unlock for preview/download
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div style={{ display: 'flex', gap: 16, height: '100%' }}>
                {/* Key Finder Sidebar */}
                <div
                  style={{
                    width: '280px',
                    border: '1px solid #e0e3ef',
                    borderRadius: 6,
                    padding: 12,
                    maxHeight: '500px',
                    overflow: 'auto',
                    backgroundColor: '#f9f9f9',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <FaSearch size={14} color="#666" />
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
                      Variables in HTML ({variablesInHTML.length})
                    </h4>
                  </div>
                  {variablesInHTML.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, color: '#999', fontSize: 12 }}>
                      No variables found
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {variablesInHTML.map((key) => {
                        const isEditable = editableInWizardKeys.includes(key);
                        const isSelected = selectedKey === key;
                        const schemaField = allSchemaFields.find((f) => f.key === key);
                        const isInSchema = !!schemaField;

                        return (
                          <div
                            key={key}
                            onClick={() => scrollToVariable(key)}
                            style={{
                              padding: '8px 10px',
                              background: isSelected ? '#e3f2fd' : '#fff',
                              border: isSelected ? '1px solid #1976d2' : '1px solid #e0e3ef',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = '#fff';
                              }
                            }}
                          >
                            <span
                              style={{
                                fontFamily: 'monospace',
                                color: isInSchema ? '#0f2b5a' : '#f57c00',
                                fontWeight: isInSchema ? 500 : 600,
                              }}
                            >
                              {key}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {isEditable && (
                                <span
                                  title="Editable in Wizard"
                                  style={{
                                    padding: '2px 6px',
                                    background: '#1976d2',
                                    color: '#fff',
                                    borderRadius: 3,
                                    fontSize: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3,
                                  }}
                                >
                                  <FaWrench size={8} />
                                  Wizard
                                </span>
                              )}
                              {!isInSchema && (
                                <span
                                  title="Not in schema"
                                  style={{
                                    padding: '2px 6px',
                                    background: '#f57c00',
                                    color: '#fff',
                                    borderRadius: 3,
                                    fontSize: 10,
                                  }}
                                >
                                  ⚠
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* HTML Editor */}
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>HTML Content</label>
                  <textarea
                    ref={htmlTextareaRef}
                    value={htmlContent}
                    onChange={(e) => {
                      setHtmlContent(e.target.value);
                      setSelectedKey(null);
                    }}
                    style={{
                      ...inputStyle,
                      minHeight: 450,
                      fontFamily: 'monospace',
                      fontSize: 13,
                      lineHeight: 1.6,
                    }}
                    placeholder="<html>...</html>"
                  />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 8, display: 'flex', gap: 16 }}>
                    <div>Use variables like {`{{field_key}}`}</div>
                    <div style={{ color: '#1976d2' }}>
                      <FaWrench size={10} style={{ marginRight: 4 }} />
                      = Editable in Wizard
                    </div>
                    <div style={{ color: '#f57c00' }}>⚠ = Not in schema</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'schema' && (
              <div>
                {/* AP-S2: Schema Family Badge */}
                {schemaFamily && (
                  <div style={{ marginBottom: 16, padding: 12, background: '#fff3e0', borderRadius: 6, border: '2px solid #ff9800' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#e65100', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Schema Family
                      </span>
                      <span style={{ padding: '4px 12px', background: '#ff9800', color: '#fff', fontSize: 13, fontWeight: 600, borderRadius: 4 }}>
                        {schemaFamily}
                      </span>
                    </div>
                    <p style={{ margin: '8px 0 0 0', fontSize: 11, color: '#bf360c' }}>
                      Nur der passende Loader wird angezeigt. Schema = HTML = Family.
                    </p>
                  </div>
                )}

                {/* AP-S2: Conditional Quick Start - Only show matching loader */}
                {(!schemaFamily || schemaFamily === 'MINIJOB_BASE_V2') && (
                  <div style={{ marginBottom: 16, padding: 16, background: '#e3f2fd', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, color: '#1565c0', fontWeight: 600 }}>
                        Quick Start: Load Minijob Base Schema (V2)
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#1976d2' }}>
                        Loads {MINIJOB_SCHEMA_V2.required_fields.length + MINIJOB_SCHEMA_V2.optional_fields.length} variables ({MINIJOB_SCHEMA_V2.required_fields.length} required, {MINIJOB_SCHEMA_V2.optional_fields.length} optional)
                      </p>
                    </div>
                    <button
                      onClick={loadMinijobSchema}
                      style={{
                        padding: '8px 16px',
                        background: '#1976d2',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <FaFileImport size={12} />
                      Load Base Schema
                    </button>
                  </div>
                )}

                {schemaFamily === 'MINIJOB_COND_V1' && (
                  <div style={{ marginBottom: 16, padding: 16, background: '#f3e5f5', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, color: '#6a1b9a', fontWeight: 600 }}>
                        Quick Start: Load Minijob COND Schema (V1)
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#7b1fa2' }}>
                        Loads Minijob COND Schema fields - inkl. § 1 Befristung + § 7 Adresse
                      </p>
                    </div>
                    <button
                      onClick={loadCondSchema}
                      style={{
                        padding: '8px 16px',
                        background: '#7b1fa2',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <FaFileImport size={12} />
                      Load COND Schema
                    </button>
                  </div>
                )}

                <div style={{ marginBottom: 32 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0f2b5a' }}>
                      Required Fields
                    </h4>
                    <button
                      onClick={() => addField(true)}
                      style={{
                        padding: '6px 12px',
                        background: '#021a4a',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <FaPlus size={10} />
                      Add Field
                    </button>
                  </div>
                  {!Array.isArray(requiredFields) || requiredFields.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, color: '#999', fontSize: 13 }}>
                      No required fields
                    </div>
                  ) : (
                    requiredFields.map((field, idx) => renderFieldEditor(field, true, idx))
                  )}
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0f2b5a' }}>
                      Optional Fields
                    </h4>
                    <button
                      onClick={() => addField(false)}
                      style={{
                        padding: '6px 12px',
                        background: '#021a4a',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <FaPlus size={10} />
                      Add Field
                    </button>
                  </div>
                  {!Array.isArray(optionalFields) || optionalFields.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, color: '#999', fontSize: 13 }}>
                      No optional fields
                    </div>
                  ) : (
                    optionalFields.map((field, idx) => renderFieldEditor(field, false, idx))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'blocks' && templateId && (
              <div>
                <div style={{ marginBottom: 16, padding: 16, background: '#e3f2fd', borderRadius: 6 }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#1565c0' }}>
                    <strong>Read-only:</strong> Blocks are automatically detected from HTML with <code>data-doc-block</code> attributes. Edit the HTML in the Content tab to modify blocks.
                  </p>
                </div>

                {!blocksSchema || blocksSchema.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 60, color: '#999', fontSize: 14 }}>
                    <p>No blocks found in this template.</p>
                    <p style={{ fontSize: 12, marginTop: 8 }}>
                      Add <code>data-doc-block="block_key"</code> attributes to HTML elements to define editable blocks.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {blocksSchema.map((block: any, idx: number) => (
                      <div
                        key={idx}
                        style={{
                          padding: 16,
                          border: '1px solid #e0e3ef',
                          borderRadius: 6,
                          background: '#f9f9f9',
                        }}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                          <div>
                            <label style={{ ...labelStyle, color: '#999' }}>Block Key</label>
                            <div style={{
                              ...inputStyle,
                              background: '#fff',
                              color: '#666',
                              fontFamily: 'monospace',
                              fontSize: 13,
                            }}>
                              {block.block_key}
                            </div>
                          </div>
                          <div>
                            <label style={{ ...labelStyle, color: '#999' }}>Label</label>
                            <div style={{
                              ...inputStyle,
                              background: '#fff',
                              color: '#666',
                            }}>
                              {block.label || '—'}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                          <div>
                            <label style={{ ...labelStyle, color: '#999' }}>Type</label>
                            <div style={{
                              ...inputStyle,
                              background: '#fff',
                              color: '#666',
                            }}>
                              {block.type}
                            </div>
                          </div>
                          <div>
                            <label style={{ ...labelStyle, color: '#999' }}>Editable By</label>
                            <div style={{
                              ...inputStyle,
                              background: block.editable_by === 'admin' ? '#ffe0b2' :
                                          block.editable_by === 'manager' ? '#fff9c4' :
                                          '#e8f5e9',
                              color: '#666',
                              fontWeight: 600,
                            }}>
                              {block.editable_by === 'admin' && '🔒 Admin Only'}
                              {block.editable_by === 'manager' && '🔧 Manager+'}
                              {block.editable_by === 'all' && '✏️ All'}
                            </div>
                          </div>
                          <div>
                            <label style={{ ...labelStyle, color: '#999' }}>Max Length</label>
                            <div style={{
                              ...inputStyle,
                              background: '#fff',
                              color: '#666',
                            }}>
                              {block.max_length || '—'}
                            </div>
                          </div>
                        </div>

                        {block.default_content && (
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ ...labelStyle, color: '#999' }}>Default Content (Preview)</label>
                            <div style={{
                              ...inputStyle,
                              background: '#fff',
                              color: '#666',
                              maxHeight: 100,
                              overflow: 'auto',
                              fontSize: 12,
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                            }}>
                              {block.default_content.substring(0, 200)}
                              {block.default_content.length > 200 && '...'}
                            </div>
                          </div>
                        )}

                        {block.help_text && (
                          <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                            💡 {block.help_text}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'blocks' && !templateId && (
              <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                Save template first to see blocks
              </div>
            )}

            {activeTab === 'bindings' && templateId && (
              <TemplateBindingsPanel templateId={templateId} />
            )}

            {activeTab === 'bindings' && !templateId && (
              <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                Save template first to add bindings
              </div>
            )}
          </>
        )}
      </div>

      <div
        style={{
          padding: 24,
          borderTop: '1px solid #e0e3ef',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
        }}
      >
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            padding: '10px 20px',
            background: '#f5f5f5',
            border: '1px solid #e0e3ef',
            borderRadius: 10,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            color: '#666',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          style={{
            padding: '10px 20px',
            background: saving || loading ? '#ccc' : '#021a4a',
            border: 'none',
            borderRadius: 10,
            cursor: saving || loading ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <FaSave size={14} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
